// ============================================================================
// Nudge — "you haven't replied yet" (detail-page brief §3)
// ============================================================================
// A CHASE is for a supplier who never sent us anything. It's the wrong word
// here: they DID send something, we asked them a question, and they've gone
// quiet. That's a nudge, and it carries the guardrails from the post-deadline
// brief §2 so it can't become nagging:
//
//   - only while a question is genuinely outstanding (we asked last, they
//     haven't answered)
//   - not before 3 WORKING days of silence — a Friday question doesn't earn a
//     Monday nudge
//   - at most once every 7 days, and the UI says when the last one went
//   - it disappears the moment they reply or upload
//
// Pure so the rules are testable; the sending lives in complianceDecisions.
// ============================================================================

export const NUDGE_MIN_WORKING_DAYS = 3;
export const NUDGE_COOLDOWN_DAYS = 7;

const dayMs = 24 * 60 * 60 * 1000;

// Whole working days (Mon–Fri) strictly after `fromIso` up to and including
// `toIso`. Public holidays aren't modelled — this is a politeness threshold,
// not a payroll calculation, and the 7-day cooldown absorbs the difference.
export function workingDaysBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 0;
  const from = new Date(`${String(fromIso).slice(0, 10)}T00:00:00Z`);
  const to = new Date(`${String(toIso).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to <= from) return 0;

  let count = 0;
  for (let t = from.getTime() + dayMs; t <= to.getTime(); t += dayMs) {
    const day = new Date(t).getUTCDay();
    if (day !== 0 && day !== 6) count++;
  }
  return count;
}

const daysBetween = (fromIso, toIso) => {
  if (!fromIso || !toIso) return Infinity;
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return Infinity;
  return Math.floor((to - from) / dayMs);
};

// Can this document be nudged, and if not, why not?
//
//   awaitingReply         — we asked something and are waiting (caller derives
//                           this from the document's state + thread)
//   lastAskedAt           — when we last asked (ISO)
//   lastSupplierActivityAt— their last reply/upload (ISO or null)
//   lastNudgeAt           — when we last nudged (ISO or null)
//
// Returns { canNudge, reason, workingDaysWaited, daysSinceNudge, availableInDays }.
export function nudgeEligibility({
  awaitingReply, lastAskedAt, lastSupplierActivityAt, lastNudgeAt, nowIso,
} = {}) {
  const now = nowIso || new Date().toISOString();

  if (!awaitingReply) {
    return { canNudge: false, reason: 'nothing_outstanding' };
  }
  // They've come back since we asked — there is nothing to nudge about, even if
  // the document still needs work.
  if (lastSupplierActivityAt && lastAskedAt && new Date(lastSupplierActivityAt) > new Date(lastAskedAt)) {
    return { canNudge: false, reason: 'they_replied' };
  }

  const workingDaysWaited = workingDaysBetween(lastAskedAt, now);
  if (workingDaysWaited < NUDGE_MIN_WORKING_DAYS) {
    return {
      canNudge: false,
      reason: 'too_soon',
      workingDaysWaited,
      availableInDays: NUDGE_MIN_WORKING_DAYS - workingDaysWaited,
    };
  }

  const daysSinceNudge = daysBetween(lastNudgeAt, now);
  if (lastNudgeAt && daysSinceNudge < NUDGE_COOLDOWN_DAYS) {
    return {
      canNudge: false,
      reason: 'cooling_off',
      workingDaysWaited,
      daysSinceNudge,
      availableInDays: NUDGE_COOLDOWN_DAYS - daysSinceNudge,
    };
  }

  return { canNudge: true, reason: 'ok', workingDaysWaited, daysSinceNudge: lastNudgeAt ? daysSinceNudge : null };
}

// The sentence the button (or its absence) should say.
//
// Returns null for the two reasons where a caller would normally HIDE the
// control entirely rather than disable it. If you render a disabled button in
// those cases, use nudgeBlockedText instead — a disabled button with no
// explanation is the worst of both, and shipped once already.
export function nudgeExplanation(eligibility) {
  if (!eligibility) return null;
  switch (eligibility.reason) {
    case 'nothing_outstanding': return null;
    case 'they_replied':        return null;
    case 'too_soon':
      return `They were asked ${eligibility.workingDaysWaited} working day${eligibility.workingDaysWaited === 1 ? '' : 's'} ago — you can nudge after ${NUDGE_MIN_WORKING_DAYS}.`;
    case 'cooling_off':
      return `Nudged ${eligibility.daysSinceNudge} day${eligibility.daysSinceNudge === 1 ? '' : 's'} ago — the next one can go in ${eligibility.availableInDays} day${eligibility.availableInDays === 1 ? '' : 's'}.`;
    default:
      return eligibility.daysSinceNudge != null
        ? `Last nudged ${eligibility.daysSinceNudge} days ago.`
        : `No reply for ${eligibility.workingDaysWaited} working days.`;
  }
}

// Always a sentence, for callers that show the control disabled rather than
// hiding it. Never returns null: if the button is on screen and can't be
// pressed, the reason has to be on screen too.
export function nudgeBlockedText(eligibility) {
  const explained = nudgeExplanation(eligibility);
  if (explained) return explained;
  switch (eligibility?.reason) {
    case 'they_replied':
      return "They've come back since you asked — there's nothing to nudge about.";
    case 'nothing_outstanding':
      return "Nothing is outstanding with the supplier, so there's nothing to nudge.";
    default:
      return null;
  }
}

// The nudge inputs, derived from a document's event stream.
//
// Extracted because two surfaces now offer Nudge (the document detail page and
// the review page) and they MUST agree. In particular they must agree on
// lastNudgeAt: without it the 7-day cooldown silently stops working and a
// supplier can be nudged twice in a day from two different screens, which is
// exactly the behaviour the guardrails exist to prevent.
//
// `stream` is loadDocumentStream's output (newest-first).
//
// `fallbackAskedAt` matters more than it looks. The DOCUMENT'S STATE is the
// authoritative fact that we asked and are waiting — the stream only tells us
// when. Documents asked before compliance_document_events existed (or whose
// marker didn't get logged) have no clarification_requested entry, and deriving
// awaitingReply from the stream alone concluded "nothing outstanding" on a
// document literally labelled "awaiting clarification since 4 Aug" — a disabled
// Nudge button on the one screen where nudging is the whole point. Pass the
// row's reviewed_at and the state wins.
export function nudgeInputsFromStream(stream = [], { withSupplier = false, fallbackAskedAt = null } = {}) {
  const lastAsk = stream.find(s => s.side === 'staff' && (s.type === 'message' || s.type === 'clarification_requested'));
  const lastSupplierActivity = stream.find(s => s.side === 'supplier');
  const lastNudge = stream.find(s => s.type === 'required' && s.meta?.nudge);
  return {
    // Waiting on them = the document is with them AND we know when we asked.
    awaitingReply: !!withSupplier && !!(lastAsk || fallbackAskedAt),
    lastAskedAt: lastAsk?.at || fallbackAskedAt || null,
    lastSupplierActivityAt: lastSupplierActivity?.at || null,
    lastNudgeAt: lastNudge?.at || null,
    // The question itself, so the nudge email can quote what was asked rather
    // than sending a bare "you haven't replied". Not used by nudgeEligibility —
    // it's passed straight through for the caller.
    lastAsk: lastAsk || null,
  };
}
